import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/index.js';

interface ToolCallCardProps {
  name: string;
  input: Record<string, unknown>;
  result?: unknown;
  isError?: boolean;
  durationMs?: number;
}

/**
 * Renders an AI tool call as an expandable line:
 *   "🔧 search_invoices ✓ 14ms"
 * with a "View" toggle that reveals the JSON args + result.
 */
export function ToolCallCard({ name, input, result, isError, durationMs }: ToolCallCardProps): JSX.Element {
  const { t } = useTranslation('ai');
  const [open, setOpen] = useState(false);
  return (
    <div
      className={[
        'rounded-md border text-xs',
        isError ? 'border-danger bg-danger/5' : 'border-border bg-bg-muted',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-start"
        aria-expanded={open}
      >
        <span aria-hidden>{isError ? '⚠️' : '🔧'}</span>
        <code className="font-mono">{name}</code>
        <Badge tone={isError ? 'danger' : result !== undefined ? 'success' : 'info'}>
          {result !== undefined ? (isError ? t('tool.error') : t('tool.ok')) : t('tool.running')}
        </Badge>
        {durationMs !== undefined && (
          <span className="ms-auto text-fg-subtle">{durationMs}ms</span>
        )}
      </button>
      {open && (
        <div className="space-y-2 border-t border-border px-3 py-2 font-mono text-[0.7rem]">
          <div>
            <span className="text-fg-subtle">{t('tool.args')}: </span>
            <pre className="overflow-x-auto whitespace-pre-wrap break-all">{JSON.stringify(input, null, 2)}</pre>
          </div>
          {result !== undefined && (
            <div>
              <span className="text-fg-subtle">{t('tool.result')}: </span>
              <pre className="overflow-x-auto whitespace-pre-wrap break-all">{JSON.stringify(result, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
